import React from 'react'
import { Box, Typography, Skeleton, Avatar } from '@mui/material'
import { isToday } from 'date-fns'
import { getProjectColorTokens } from './projectCalendarColors'

const getTitle = (e) => e?.title || e?.name || e?.project_name || 'Untitled'

const getImgUrl = (staff) =>
    staff?.staff_profile_image?.profile_image_url ??
    staff?.profile_image_url ??
    null

const getInitials = (name = '') => {
    const parts = String(name).trim().split(/\s+/).filter(Boolean)
    if (parts.length === 0) return '??'
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
    return (parts[0][0] + parts[1][0]).toUpperCase()
}

const dedupeStaff = (list = []) => {
    const map = new Map()

    list.filter(Boolean).forEach((s) => {
        const key =
            s?.id ??
            s?.user_id ??
            (s?.work_email ? `email:${s.work_email}` : null) ??
            (s?.name ? `name:${s.name}` : null)

        if (!key) return
        if (!map.has(key)) map.set(key, s)
    })

    return Array.from(map.values())
}

const getProjectStaff = (event) => {
    const members = event?.members || event?.project_members || []
    const staffList = members.map((m) => m?.staff).filter(Boolean)
    return dedupeStaff(staffList)
}

const getSelectedStaffInProject = (event, selectedEmployees = []) => {
    if (!selectedEmployees?.length) return []

    // dedupe selectedEmployees too (safety)
    const selectedUnique = dedupeStaff(selectedEmployees)
    const selectedIds = new Set(selectedUnique.map((s) => s?.id).filter(Boolean))

    const projectStaff = getProjectStaff(event) // already deduped
    return projectStaff.filter((s) => selectedIds.has(s.id))
}

const StackedAvatars = ({ staff = [] }) => {
    const uniqueStaff = dedupeStaff(staff)
    if (!uniqueStaff.length) return null

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', ml: 1, flexShrink: 0 }}>
            {uniqueStaff.slice(0, 3).map((s, i) => {
                const src = getImgUrl(s)
                const initials = getInitials(s?.name)

                return (
                    <Avatar
                        key={s?.id ?? s?.user_id ?? `${s?.name ?? 'staff'}-${i}`}
                        src={src || undefined}
                        imgProps={{ referrerPolicy: 'no-referrer' }}
                        sx={{
                            width: 22,
                            height: 22,
                            fontSize: 10,
                            bgcolor: '#111',
                            border: '2px solid #fff',
                            ml: i === 0 ? 0 : -0.8,
                        }}
                    >
                        {!src ? initials : null}
                    </Avatar>
                )
            })}
        </Box>
    )
}

export default function TrackerDayDiv({
    day,
    index,
    isLoading,
    events = [],
    onDayClick,
    onEventClick,
    showMembers = false,
    members = [],
}) {
    const isInactiveCell = day?.outsideMonth
    const MAX_EVENTS = 3
    const isCurrentDay = day?.object ? isToday(day.object) : false

    return (
        <Box
            sx={{
                width: 'calc(100% / 7)',
                height: { xs: '140px', sm: '180px' },
                borderLeft: '1px solid #E5E5E5',
                borderTop: '1px solid #E5E5E5',
                background: isInactiveCell
                    ? '#F6F6F6'
                    : isCurrentDay
                        ? 'rgba(243,191,58,0.55))'
                        : 'white',
                p: 1,
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                gap: 0.75,
                boxShadow: isCurrentDay ? 'inset 0 0 0 1px rgba(243,191,58,0.55)' : 'none',
                '&:hover': { background: isInactiveCell ? '#F6F6F6' : 'rgba(0,0,0,0.02)' },
            }}
            onClick={() => {
                onDayClick?.(day?.dateObj ?? new Date(day?.fullDate), events)
            }}
        >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography
                    variant="body2"
                    fontWeight={700}
                    sx={{
                        minWidth: 28,
                        height: 28,
                        px: 0.75,
                        borderRadius: 999,
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: isCurrentDay ? '#F3BF3A' : events.length ? 'rgba(17,24,39,0.05)' : 'transparent',
                        color: isCurrentDay ? '#111827' : '#111827',
                    }}
                >
                    {day?.date || day?.day || day?.angka || ''}
                </Typography>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, overflow: 'hidden' }}>
                {isLoading ? (
                    <>
                        <Skeleton variant="rounded" height={18} />
                        <Skeleton variant="rounded" height={18} />
                    </>
                ) : (
                    <>
                        {events.slice(0, MAX_EVENTS).map((e, idx) => {
                            const staffToShow = showMembers ? getSelectedStaffInProject(e, members) : []
                            const colorTokens = getProjectColorTokens(e)

                            return (
                                <Box
                                    key={e?.id ?? idx}
                                    onClick={(ev) => {
                                        ev.stopPropagation()
                                        onEventClick?.(e)
                                    }}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        px: 1,
                                        py: 0.75,
                                        borderRadius: 1.5,
                                        border: `1px solid ${colorTokens.accentBorder}`,
                                        cursor: 'pointer',
                                        '&:hover': {
                                            background: colorTokens.accentSurface,
                                            transform: 'translateY(-1px)',
                                            boxShadow: `0 8px 20px ${colorTokens.accentSoft}`,
                                        },
                                        transition: 'all 0.18s ease',
                                        background: colorTokens.accentSurface,
                                        minWidth: 0,
                                        boxShadow: `0 1px 2px ${colorTokens.accentSoft}`,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: 4,
                                            height: 20,
                                            borderRadius: 999,
                                            background: colorTokens.accent,
                                            flexShrink: 0,
                                        }}
                                    />

                                    <Box sx={{ minWidth: 0, flex: 1 }}>
                                        <Typography
                                            variant="caption"
                                            fontWeight={700}
                                            title={getTitle(e)}
                                            sx={{
                                                display: 'block',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                                minWidth: 0,
                                                color: colorTokens.accentText,
                                                letterSpacing: '-0.01em',
                                            }}
                                        >
                                            {getTitle(e)}
                                        </Typography>
                                    </Box>

                                    {showMembers ? <StackedAvatars staff={staffToShow} /> : null}
                                </Box>
                            )
                        })}

                        {events.length > MAX_EVENTS ? (
                            <Typography variant="caption" color="text.secondary" sx={{ px: 0.5 }}>
                                +{events.length - MAX_EVENTS} more
                            </Typography>
                        ) : null}
                    </>
                )}
            </Box>
        </Box>
    )
}
